import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, Navigate, useParams } from "react-router-dom";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "../../Assests/Css/blog.css";
import usrDummy from "../../Assests/images/user_dh.png";
import wLogo from "../../Assests/images/w_logo.png";
import Skeleton from "../../Components/Common/SkeletonLoader/SkeletonLoader";
import { SetMetaTags } from "../../Components/Utility/setMetaTags";
import LoginModal from "../Users/LoginModal";

// Import local images from src/Assests/images/
import detail1 from "../../Assests/images/detail1.png";
import detail2 from "../../Assests/images/detail2.png";
import detailcard from "../../Assests/images/detailcard.png";

const localImages = {
  "/images/detail1.png": detail1,
  "/images/detail2.png": detail2,
  "/images/detailcard.png": detailcard,
};

const resolveImage = (path) => {
  if (path && localImages[path]) {
    return localImages[path];
  }
  return process.env.PUBLIC_URL + path;
};

function BlogDetail() {
  const { slug } = useParams();
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(124);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [heroImageError, setHeroImageError] = useState(false);
  const [relatedImageErrors, setRelatedImageErrors] = useState({});
  const baseUrl = process.env.REACT_APP_BASE_URL || process.env.REACT_APP_API_BASE?.replace("/api", "/");
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    let lastToken = localStorage.getItem("authToken");

    const checkToken = () => {
      const currentToken = localStorage.getItem("authToken");
      if (currentToken && currentToken !== lastToken) {
        window.location.reload(); // Reload once after login/OTP
      }
      lastToken = currentToken;
    };

    const interval = setInterval(checkToken, 1000);
    return () => clearInterval(interval);
  }, []);


  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).toUpperCase();
    } catch (e) {
      return dateString;
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });

    const fetchPostFromApi = async () => {
      const token = localStorage.getItem("authToken");
      try {
        setLoading(true);
        const res = await axios.get(`${process.env.REACT_APP_API_BASE}/blogs/${slug}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.data && res.data.status) {
          const apiPost = res.data.data;
          if (apiPost) {
            setPost(apiPost);
            setLiked(apiPost.is_liked === 1);
            setLikesCount(apiPost.likes_count || 0);
            setRelatedPosts(res.data.mostlike || []);
            setGallery(res.data.gallery_api || apiPost.gallery_api || []);
          } else {
            setPost(null);
          }
        }
      } catch (err) {
        console.error("Error fetching blog detail:", err);
        setPost(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPostFromApi();
  }, [slug]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied to clipboard!");
  };

  const handleLikeToggle = async () => {
    if (!post || !post.id) return;
    const token = localStorage.getItem("authToken");
    if (!token) {
      setLoginOpen(true);
      return;
    }

    const newLiked = !liked;
    setLiked(newLiked);
    setLikesCount((prev) => (newLiked ? prev + 1 : prev - 1));

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/blogs/${post.id}/like`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.data && res.data.status) {
        toast.success(res.data.message || "Like status updated!");
        if (res.data.likes_count !== undefined) {
          setLikesCount(res.data.likes_count);
        }
      } else {
        // Revert on error
        setLiked(!newLiked);
        setLikesCount((prev) => (!newLiked ? prev + 1 : prev - 1));
        toast.error(res.data.message || "Failed to update like status.");
      }
    } catch (err) {
      // Revert on error
      setLiked(!newLiked);
      setLikesCount((prev) => (!newLiked ? prev + 1 : prev - 1));
      console.error("Error liking blog:", err);
      toast.error(err.response?.data?.message || "Failed to update like status.");
    }
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.trim()) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    if (email.length >= 60) {
      setEmailError("Email address must not exceed 50 characters.");
      return;
    }
    if (!emailRegex.test(email.trim())) {
      setEmailError("Please enter a valid email address (e.g. name@example.com).");
      return;
    }
    setSubscribeLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE}/subscribe-now`, {
        email: email.trim(),
      });
      if (res.data && res.data.status) {
        toast.success(res.data.message || "Thank you for joining our weekly discovery!");
        setEmail("");
        setEmailError("");
      } else {
        toast.error(res.data.message || "Subscription failed. Please try again.");
      }
    } catch (err) {
      console.error("Error subscribing:", err);
      toast.error(
        err.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setSubscribeLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="journal-page">
        {/* Skeleton Hero Section */}
        <div className="blog-detail-hero" style={{ backgroundImage: "none", minHeight: "500px", height: "700px", backgroundColor: "#f4f5f6" }}>
          <div className="hero-overlay"></div>
          <div className="container" style={{ width: "100%", zIndex: 2 }}>
            <div className="blog-detail-hero-content text-left">
              <Skeleton width="80px" height="24px" style={{ marginBottom: "20px" }} />
              <Skeleton width="70%" height="48px" style={{ marginBottom: "15px" }} />
              <Skeleton width="40%" height="48px" style={{ marginBottom: "20px" }} />
              <Skeleton width="200px" height="20px" />
            </div>
          </div>
        </div>

        {/* Skeleton Article Body */}
        <div className="blog-detail-container">
          <div className="blog-detail-body mt-5">
            <Skeleton width="100%" height="20px" style={{ marginBottom: "15px" }} />
            <Skeleton width="95%" height="20px" style={{ marginBottom: "15px" }} />
            <Skeleton width="90%" height="20px" style={{ marginBottom: "15px" }} />
            <Skeleton width="95%" height="20px" style={{ marginBottom: "15px" }} />
            <Skeleton width="60%" height="20px" style={{ marginBottom: "30px" }} />

            <Skeleton width="40%" height="30px" style={{ marginBottom: "20px", marginTop: "30px" }} />
            <Skeleton width="100%" height="20px" style={{ marginBottom: "15px" }} />
            <Skeleton width="90%" height="20px" style={{ marginBottom: "15px" }} />
            <Skeleton width="95%" height="20px" style={{ marginBottom: "15px" }} />
          </div>
        </div>
      </div>
    );
  }

  // Handle case where post is not found
  if (!post) {
    return <Navigate to="/404" replace />;
  }



  const getPostHeroImage = () => {
    if (!post) return "";
    const imgPath = post.hero_banner || post.image;
    if (!imgPath) return "";
    if (imgPath.startsWith("http") || imgPath.startsWith("data:")) return imgPath;
    if (localImages[imgPath]) return localImages[imgPath];
    if (post.hero_banner) return `${baseUrl}${post.hero_banner}`;
    return process.env.PUBLIC_URL + imgPath;
  };

  const authorName = post.author && typeof post.author === "object"
    ? post.author.adminName
    : (post.author || "WeyFarin");

  const postDate = post.date || formatDate(post.publish_date);

  const authorInfo = post.authorInfo || (post.author && typeof post.author === "object" ? {
    name: post.author.adminName,
    avatar: post.author.profile ? (post.author.profile.startsWith("http") ? post.author.profile : `${baseUrl}${post.author.profile}`) : "/images/detailcard.png",
    bio: post.author.about_me || "Weyfarin Author"
  } : null);

  const displayTag = (post.tag || post.categories?.[0]?.title || post.tags || "Travel").toUpperCase();

  const siteOrigin = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/+$/, "");

  return (
    <div className="journal-page">
      <SetMetaTags
        title={`${post.title} - The Weyfarin Journal`}
        description={post.short_description || post.description}
        keywords={`travel, blog, ${displayTag.toLowerCase()}, ${post.title.toLowerCase()}, weyfarin`}
        canonicalUrl={`${siteOrigin}/blogs/${post.slug}`}
        ogTitle={`${post.title} - The Weyfarin Journal`}
        ogDescription={post.short_description || post.description}
        ogImage={getPostHeroImage()}
        ogUrl={`${siteOrigin}/blogs/${post.slug}`}
      />

      {/* Hero Section (Slider or Static Image) */}
      {gallery && gallery.length > 0 ? (
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={0}
          slidesPerView={1}
          loop={true}
          autoplay={{ delay: 3000, disableOnInteraction: false }}
          // navigation
          // pagination={{ clickable: true }}
          className="blog-detail-hero-swiper"
        >
          {gallery.map((item, index) => (
            <SwiperSlide key={item.id || index}>
              <div
                className="blog-detail-hero"
                style={{
                  backgroundImage: `url(${item.image && (item.image.startsWith("http") || item.image.startsWith("data:")) ? item.image : `${baseUrl}${item.image}`})`
                }}
              >
                <div className="hero-overlay"></div>
                <div className="container">
                  <div className="blog-detail-hero-content text-left">
                    <span className="detail-hero-tag">{displayTag}</span>
                    <h1>{post.title}</h1>
                    <div className="blog-detail-meta">
                      By {authorName} &nbsp;•&nbsp; {postDate}
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <div
          className="blog-detail-hero"
          style={getPostHeroImage() && !heroImageError ? { backgroundImage: `url(${getPostHeroImage()})` } : {}}
        >
          {getPostHeroImage() && !heroImageError && (
            <img
              src={getPostHeroImage()}
              alt={post.title || "Blog Hero Image"}
              title={post.title || "Blog Hero Image"}
              style={{ display: "none" }}
              onError={() => setHeroImageError(true)}
            />
          )}
          {(!getPostHeroImage() || heroImageError) && (
            <div className="placeholder-logo-overlay">
              <img src={wLogo} alt="Weyfarin Logo" title="Weyfarin Logo" className="placeholder-logo" />
            </div>
          )}
          <div className="hero-overlay"></div>
          <div className="container">
            <div className="blog-detail-hero-content text-left">
              <span className="detail-hero-tag">{displayTag}</span>
              <h1>{post.title}</h1>
              <div className="blog-detail-meta">
                By {authorName} &nbsp;•&nbsp; {postDate}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Article Body Content */}
      <div className="blog-detail-container">
        {/* <div className="blog-detail-body">
          {post.content && post.content.length > 0 ? (
            post.content.map((block, idx) => {
              if (block.type === "heading") {
                return <h2 key={idx}>{block.value}</h2>;
              } else if (block.type === "quote") {
                return (
                  <div key={idx} className="blog-detail-quote-section text-center">
                    <hr className="quote-divider" />
                    <span className="quote-subtitle">{block.subtitle}</span>
                    <blockquote className="blog-detail-blockquote border-0">
                      "{block.value}"
                    </blockquote>
                    <hr className="quote-divider" />
                  </div>
                );
              } else if (block.type === "images") {
                return (
                  <div key={idx} className="blog-detail-images-row row my-2">
                    {block.value.map((imgSrc, imgIdx) => (
                      <div key={imgIdx} className="col-md-6 col-sm-12 mb-4 mb-md-0">
                        <div className="balkan-inline-image-wrapper">
                          <img
                            src={resolveImage(imgSrc)}
                            alt={`Balkans illustration ${imgIdx + 1}`}
                            title={`Balkans illustration ${imgIdx + 1}`}
                            className="img-fluid balkan-inline-image"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }
              return <p key={idx}>{block.value}</p>;
            })
          ) : (
            Fallback paragraph if no content block structured
            <p>{post.related_content || post.short_description || post.description}</p>
          )}
        </div> */}

        <div className="blog-detail-container">
  <div className="blog-detail-body">

    {/* Short Description */}
    {post.short_description && (
      <p>{post.short_description}</p>
    )}

    {/* Blog Content */}
    {post.contents_api?.length > 0 &&
      post.contents_api.map((block) => (
        <div
          key={block.id}
          className="blog-content-block"
          dangerouslySetInnerHTML={{ __html: block.content }}
        />
      ))}

    {/* Related Content */}
    {post.related_content && (
  <div className="blog-detail-quote-section text-center my-4">
    <blockquote className="blog-detail-blockquote border-0">
      "{post.related_content}"
    </blockquote>
  </div>
)}

  </div>
</div>

        {/* About the Author Card */}
        {authorInfo && (
          <div className="author-card-box my-5 py-4 px-2">
            <div className="row align-items-center">
              <div className="col-md-3 col-sm-4 text-center mb-3 mb-md-0">
                <div className="author-avatar-wrapper">
                  <img
                    src={
                      authorInfo.avatar.startsWith("http") || authorInfo.avatar.startsWith("data:")
                        ? authorInfo.avatar
                        : resolveImage(authorInfo.avatar)
                    }
                    alt={authorInfo.name}
                    title={authorInfo.name}
                    className="author-avatar-img img-fluid"
                    onError={(e) => { e.target.onerror = null; e.target.src = usrDummy; }}
                  />
                </div>
              </div>
              <div className="col-md-9 col-sm-8 text-left">
                <span className="author-card-label">ABOUT THE AUTHOR</span>
                <h4 className="author-card-name my-1">{authorInfo.name}</h4>
                <p className="author-card-bio mb-0">{authorInfo.bio}</p>
              </div>
            </div>
          </div>
        )}

        {/* Share and Likes Actions Bar */}
        <hr className="mb-4" />
        <div className="blog-actions-bar d-flex justify-content-start align-items-center mb-5">
          <button className="blog-action-btn share-btn" onClick={handleShare}>
            <i className="fa fa-share-alt mr-2" aria-hidden="true"></i> Share
          </button>
          <button
            className={`blog-action-btn like-btn ${liked ? 'liked' : ''}`}
            onClick={handleLikeToggle}
          >
            <i className={liked ? "fa fa-heart mr-2 text-danger" : "fa fa-heart-o mr-2"} aria-hidden="true"></i>
            <span>{likesCount}</span>
          </button>
        </div>
      </div>

      {/* Related Articles ("You might also like") Section */}
      {relatedPosts.length > 0 && (
        <div className="related-section">
          <div className="related-header">
            <h2>You might also like</h2>
            <Link to="/blogs" className="explore-more-link">
              Explore more
            </Link>
          </div>

          <div className="row">
            {relatedPosts.map((rPost) => {
              return (
                <div className="col-lg-4 col-md-6 col-sm-12 mb-4" key={rPost.id}>
                  <Link to={`/blogs/${rPost.slug}`} className="blog-grid-card">
                    <div className="blog-grid-image-wrapper">
                      {Number(rPost.is_featured) === 1 && (
                        <div className="blog-image-overlay-tags-container">
                          <span className="blog-image-overlay-tag">
                            FEATURED
                          </span>
                        </div>
                      )}
                      {(rPost.hero_banner || rPost.image) && !relatedImageErrors[rPost.id] ? (
                        <img
                          src={
                            rPost.hero_banner
                              ? rPost.hero_banner.startsWith("http")
                                ? rPost.hero_banner
                                : `${baseUrl}${rPost.hero_banner}`
                              : resolveImage(rPost.image)
                          }
                          alt={rPost.title}
                          title={rPost.title}
                          onError={() => {
                            setRelatedImageErrors((prev) => ({ ...prev, [rPost.id]: true }));
                          }}
                        />
                      ) : (
                        <div className="placeholder-image-wrapper">
                          <img src={wLogo} alt="Weyfarin Logo" title="Weyfarin Logo" className="placeholder-logo" />
                        </div>
                      )}
                    </div>
                    <h3 className="blog-card-title">{rPost.title}</h3>
                    <p className="blog-card-summary">
                      {rPost.short_description || rPost.description}
                    </p>
                    <div className="blog-card-footer">
                      {rPost.date || formatDate(rPost.publish_date)} &nbsp;&middot;&nbsp; {rPost.readTime || (rPost.min_read ? `${rPost.min_read} MIN READ` : "5 MIN READ")}
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Weekly Discovery subscription box */}
      <div className="weekly-discovery-section text-center container">
        <span className="weekly-discovery-label">THE WEEKLY DISCOVERY</span>
        <h2 className="weekly-discovery-title my-3">Stories that inspire you to pack your bags.</h2>
        <form className="weekly-discovery-form mx-auto my-4" onSubmit={handleSubscribe}>
          <div className="discovery-input-wrapper flex-wrap">
            <input
              type="email"
              placeholder="Enter your email"
              className="weekly-discovery-input"
              value={email}
              maxLength={50}
              onChange={(e) => {
                const val = e.target.value;
                setEmail(val);
                if (!val) {
                  setEmailError("");
                } else if (val.length >= 60) {
                  setEmailError("Email address must not exceed 50 characters.");
                } else if (val.includes("@") && !emailRegex.test(val)) {
                  setEmailError("Please enter a valid email address (e.g. name@example.com).");
                } else {
                  setEmailError("");
                }
              }}
              required
            />
            <button className="weekly-discovery-btn" type="submit" disabled={subscribeLoading}>
              {subscribeLoading ? "SUBSCRIBING..." : "SUBSCRIBE"}
            </button>
            {emailError && (
              <p className="text-danger small mt-1 text-start w-100">
                {emailError}
              </p>
            )}
          </div>
        </form>
        <span className="weekly-discovery-note">No spam, just inspiration. Unsubscribe anytime.</span>
      </div>
      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}


export default BlogDetail;
